/**
 * Letter refinement mutation + URL builders for the static download
 * endpoints (PDF / DOCX) and the per-document preview iframe. These
 * endpoints don't return JSON, so they don't need react-query hooks.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
    listRevisionsApiSessionsSessionIdLetterRevisionsGet,
    revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet,
    useManualSaveApiSessionsSessionIdLetterManualSavePost,
    useRefineApiSessionsSessionIdLetterRefinePost,
    useSelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost,
} from '@/codegen/openapi/client';
import { QUERY_KEY } from '@/config/constants';
import { deferredInvalidateQueries } from './utils/deferredInvalidation';
import type {
    LetterDiff,
    LetterRevisionDetail,
    LetterRevisionInfo,
    RefineResult,
} from '@/api/types';

interface RefineLetterVariables {
    sessionId: string;
    instruction: string;
}

export function useRefineLetter() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useRefineApiSessionsSessionIdLetterRefinePost();

    const refine = useCallback(
        async ({ sessionId, instruction }: RefineLetterVariables): Promise<RefineResult> => {
            const r = await mutateAsync({ sessionId, data: { instruction } });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Refine failed');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY.REVISIONS, sessionId] });
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutateAsync, queryClient],
    );

    return useMemo(() => ({ refineLetter: refine, isPending }), [refine, isPending]);
}

// ---- Revisions -----------------------------------------------------------

export function useRevisions(sessionId: string | null) {
    return useQuery({
        queryKey: [QUERY_KEY.REVISIONS, sessionId],
        enabled: !!sessionId,
        queryFn: async (): Promise<LetterRevisionInfo[]> => {
            if (!sessionId) return [];
            const r = await listRevisionsApiSessionsSessionIdLetterRevisionsGet(sessionId);
            if (!r.data) throw new Error(r.error?.toString() ?? 'Failed to load revisions');
            return r.data;
        },
    });
}

interface SelectRevisionVariables {
    sessionId: string;
    revisionId: string;
}

export function useSelectRevision() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } =
        useSelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost();

    const select = useCallback(
        async ({ sessionId, revisionId }: SelectRevisionVariables): Promise<LetterRevisionDetail> => {
            const r = await mutateAsync({ sessionId, revisionId });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Failed to select revision');
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutateAsync, queryClient],
    );

    return useMemo(() => ({ selectRevision: select, isPending }), [select, isPending]);
}

interface ManualSaveVariables {
    sessionId: string;
    html: string;
    changeSummary?: string;
}

export function useManualSave() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } =
        useManualSaveApiSessionsSessionIdLetterManualSavePost();

    const save = useCallback(
        async ({ sessionId, html, changeSummary }: ManualSaveVariables): Promise<LetterRevisionInfo> => {
            const r = await mutateAsync({
                sessionId,
                data: { html, change_summary: changeSummary ?? null },
            });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Failed to save');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY.REVISIONS, sessionId] });
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutateAsync, queryClient],
    );

    return useMemo(() => ({ manualSave: save, isPending }), [save, isPending]);
}

export function useRevisionDiff(sessionId: string | null, revisionId: string | null) {
    return useQuery({
        queryKey: [QUERY_KEY.REVISION_DIFF, sessionId, revisionId],
        enabled: !!sessionId && !!revisionId,
        queryFn: async (): Promise<LetterDiff> => {
            if (!sessionId || !revisionId) throw new Error('Missing ids');
            const r = await revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet(
                sessionId,
                revisionId,
            );
            if (!r.data) throw new Error(r.error?.toString() ?? 'Failed to load diff');
            return r.data;
        },
    });
}

// ---- URL builders (raw responses — not JSON, not react-query) -------------

export function previewUrl(sessionId: string, documentId: string): string {
    return `/api/sessions/${sessionId}/documents/${documentId}/preview.html`;
}

export function documentDownloadUrl(sessionId: string, documentId: string): string {
    return `/api/sessions/${sessionId}/documents/${documentId}/download`;
}

export function documentsZipUrl(sessionId: string): string {
    return `/api/sessions/${sessionId}/documents.zip`;
}

export function letterPdfUrl(sessionId: string): string {
    return `/api/sessions/${sessionId}/letter.pdf`;
}

export function letterDocxUrl(sessionId: string): string {
    return `/api/sessions/${sessionId}/letter.docx`;
}
